import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router, ActivatedRoute } from "@angular/router";
import { PreplanRescueService } from "../../services/preplanrescue/preplan-rescue.service";
import {
  IPreplanRescue,
  PortalSize,
  PreplanConfiguration,
} from "../../interfaces/ert-preplan-rescue/ert-preplan-rescue";
import { ILoggedUserData } from "../../interfaces/common/loggedUserData";
import { LoggedUserService } from "../../services/common/logged-user.service";

@Component({
  selector: "app-edit-ert-preplan-rescue",
  templateUrl: "./edit-ert-preplan-rescue.component.html",
  styles: [
    `
      .button-container {
        display: flex;
        justify-content: space-between;
      }
      .invalid-field {
        border: 1px solid red !important;
        box-shadow: 0 0 0 0.2rem rgba(255, 0, 0, 0.25);
      }
      .mandatory {
        color: red;
      }
      .print-alert {
        margin-top: 1rem;
        padding: 0.75rem;
        background-color: #f8d7da;
        color: #721c24;
        border: 1px solid #f5c6cb;
        border-radius: 0.25rem;
      }
    `,
  ],
})
export class EditErtPreplanRescueComponent implements OnInit {
  preplanRescueForm: FormGroup;
  formSubmitted: boolean = false;
  preplanId: number = 0;
  portalSizes = PortalSize;
  configurations = PreplanConfiguration;
  validationMessages: string[] = [];
  isLoading: boolean = true;
  editedPreplanRescue: IPreplanRescue | undefined;
  currentUser: ILoggedUserData;
  isPrinting: boolean = false;
  printMessage: string = "";
  savedSuccessMessage: string = "";

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private service: PreplanRescueService,
    private loggedUser: LoggedUserService
  ) {
    this.preplanRescueForm = this.fb.group({
      // Basic information for edit mode
      id: [0],
      lastModified: [""],

      // Section 1 - Completed by Maintenance
      date: ["", Validators.required],
      durationOfWork: ["", Validators.required],
      reasonForEntry: ["", Validators.required],
      maintsRep: ["", Validators.required],

      // Section 2 - Completed by Operations
      areaSTL: [""],
      hazardAssessment: [""],
      areaHO: [""],
      spaceDescription: [""],

      // Section 3 - Completed by Chevron Rescue Technician
      // Space Category
      categoryRA: [false],
      categoryRS: [false],

      spaceInfo: [""],

      // How is it accessed?
      accessHorizontal: [false],
      accessVerticalTop: [false],
      accessVerticalBottom: [false],
      accessElevated: [false],

      portalSize: ["1"],
      configuration: ["1"],

      // Medical Packaging Information
      longSpineBoard: [false],
      halfBack: [false],
      stokesBasket: [false],
      breathingAir: [false],

      // Rescue Method Haul System
      externalRetrieval: [false],
      threeOne: [false],
      internalRetrieval: [false],
      fiveOne: [false],
      rescueTechDiscretion: [false],
      rescueMethodDescription: [""],
    });
  }

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      if (params["id"]) {
        this.preplanId = +params["id"];
        this.loggedUser.getLoggedUserData().subscribe((user) => {
          this.currentUser = user;
          this.loadPreplanData(this.preplanId);
        });
      }
    });
  }

  /**
   * For facility id 2 the section headers are shown without the
   * "- Completed by ..." suffix.
   */
  get hideSectionSubtitles(): boolean {
    return Number(this.currentUser?.facilityId) === 2;
  }

  loadPreplanData(id: number): void {
    this.service
      .getPreplanRescueById(Number(this.currentUser?.facilityId), id)
      .subscribe((data: IPreplanRescue) => {
        // First reset the form to avoid any issues with nested form groups
        this.preplanRescueForm.reset();
        this.editedPreplanRescue = data;

        if (Number(this.currentUser?.facilityId) !== data.facilityId) {
          this.router.navigate(["/no-access"]);
          return;
        }

        // Update the form with the retrieved data using patchValue
        this.preplanRescueForm.patchValue({
          // Basic information
          id: data.id,
          lastModified: new Date(data.dateTimeAdd).toLocaleDateString(),

          // Section 1 - Completed by Maintenance
          date: new Date(data.dateTimeAdd).toISOString().split("T")[0],
          durationOfWork: data.durationOfWork,
          reasonForEntry: data.reasonOfEntry,
          maintsRep: data.maintsRepOriginator,

          // Section 2 - Completed by Operations
          areaSTL: data.areaSTL,
          hazardAssessment: data.hazardAssignments,
          areaHO: data.areaHO,
          spaceDescription: data.descriptionConfinedSpace,

          // Section 3 - Completed by Chevron Rescue Technician
          // Space Category
          categoryRA: data.spaceCategory?.isRa || false,
          categoryRS: data.spaceCategory?.isRs || false,

          spaceInfo: data.spaceInformation,

          // How is it accessed?
          accessHorizontal: data.accessMethod?.isHorizontal || false,
          accessVerticalTop: data.accessMethod?.isVerticalTop || false,
          accessVerticalBottom: data.accessMethod?.isVerticalBottom || false,
          accessElevated: data.accessMethod?.isAccessElevated || false,

          portalSize: data.portalSize || "1",
          configuration: data.configuration || "1",

          // Medical Packaging Information
          longSpineBoard: data.medicalPackingInfo?.isLongSpineBoard || false,
          halfBack: data.medicalPackingInfo?.isKED || false,
          stokesBasket: data.medicalPackingInfo?.isStokesBasket || false,
          breathingAir:
            data.medicalPackingInfo?.isBreathingAirRequired || false,

          // Rescue Method Haul System
          externalRetrieval:
            data.rescueMethodHaulSystem?.isExternalRetrieval || false,
          threeOne: data.rescueMethodHaulSystem?.is31 || false,
          internalRetrieval:
            data.rescueMethodHaulSystem?.isInternalRetrieval || false,
          fiveOne: data.rescueMethodHaulSystem?.is51 || false,
          rescueTechDiscretion:
            data.rescueMethodHaulSystem?.isRescueTechDiscretion || false,
          rescueMethodDescription: data.rescueTechDescription || "",
        });

        this.isLoading = false;
      });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.preplanRescueForm.get(fieldName);
    return this.formSubmitted && field && field.invalid;
  }

  submit(): void {
    this.formSubmitted = true;
    this.isLoading = true;
    this.validationMessages = this.validateForm();

    if (this.validationMessages.length > 0) {
      this.isLoading = false;
      return;
    }

    if (this.preplanRescueForm.valid && this.validationMessages.length === 0) {
      const formValues = this.preplanRescueForm.value;
      console.log("Form Values:", formValues);
      const formData: IPreplanRescue = {
        id: this.preplanId,
        dateTimeAdd: formValues.date ? new Date(formValues.date) : new Date(),
        durationOfWork: formValues.durationOfWork,
        reasonOfEntry: formValues.reasonForEntry,
        maintsRepOriginator: formValues.maintsRep,

        // Section 2 fields
        areaSTL: formValues.areaSTL,
        hazardAssignments: formValues.hazardAssessment,
        areaHO: formValues.areaHO,
        descriptionConfinedSpace: formValues.spaceDescription,

        // Section 3 fields
        spaceCategory: {
          isRa: formValues.categoryRA,
          isRs: formValues.categoryRS,
        },

        spaceInformation: formValues.spaceInfo,

        accessMethod: {
          isHorizontal: formValues.accessHorizontal,
          isVerticalTop: formValues.accessVerticalTop,
          isVerticalBottom: formValues.accessVerticalBottom,
          isAccessElevated: formValues.accessElevated,
        },

        portalSize: Number(formValues.portalSize),
        configuration: Number(formValues.configuration),

        medicalPackingInfo: {
          isLongSpineBoard: formValues.longSpineBoard,
          isKED: formValues.halfBack,
          isStokesBasket: formValues.stokesBasket,
          isBreathingAirRequired: formValues.breathingAir,
        },

        rescueMethodHaulSystem: {
          isExternalRetrieval: formValues.externalRetrieval,
          is31: formValues.threeOne,
          isInternalRetrieval: formValues.internalRetrieval,
          is51: formValues.fiveOne,
          isRescueTechDiscretion: formValues.rescueTechDiscretion,
        },

        rescueTechDescription: formValues.rescueMethodDescription,
        facility: undefined,
        facilityId: this.editedPreplanRescue?.facilityId || 0,
        createdDate: this.editedPreplanRescue?.createdDate || new Date(),
        createdBy: this.editedPreplanRescue?.createdBy,
        createdById: this.editedPreplanRescue?.createdById,
        modifiedById: this.currentUser?.id,
        modifiedDate: new Date(),
      };

      if (this.preplanId) {
        // Update existing preplan rescue
        this.service.updatePreplanRescue(formData).subscribe((data) => {
          this.isLoading = false;
          this.editedPreplanRescue = data;
        });
      }
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.preplanRescueForm.controls).forEach((key) => {
        const control = this.preplanRescueForm.get(key);
        control?.markAsTouched();
      });
    }
  }

  validateForm(): string[] {
    return this.service.validatePreplanRescueForm(this.preplanRescueForm);
  }

  cancel(): void {
    this.router.navigate(["/preplan-rescue"]);
  }

  printPreplanRescue(): void {
    const facilityName = this.currentUser?.facilityName;

    // Set loading and printing states
    this.isLoading = true;
    this.isPrinting = true;
    this.printMessage =
      "The print window is open. Please complete or cancel printing to continue editing. Closing a tab may cause the view to freeze for a while. Please use the cancel button.";

    // Use setTimeout to allow the UI to update before heavy print operation
    setTimeout(() => {
      this.service.printPreplanRescue(this.editedPreplanRescue, facilityName);

      // Check periodically if print window is still open
      const checkPrintWindow = setInterval(() => {
        // Reset states after a reasonable timeout or when printing is likely done
        if (!this.isPrinting) {
          clearInterval(checkPrintWindow);
        }
      }, 1000);

      // Set a timeout to reset the states after a reasonable period
      setTimeout(() => {
        this.isLoading = false;

        // Keep the print message visible for a bit longer
        setTimeout(() => {
          this.isPrinting = false;
          this.printMessage = "";
        }, 3000);
      }, 2000);
    }, 100);
  }
}
